import {
  BillingMethodList,
  BillingMethodListItem,
} from 'billing/components/BillingMethodList'
import {
  ContentEmpty,
  ContentLayout,
  ContentMain,
  ContentTip,
} from 'components/ContentLayout'
import {
  DropdownItem,
  DropdownItemDescription,
  DropdownItemTitle,
} from 'components/Dropdown'
import ListHeader from 'components/ListHeader'
import React from 'react'
import { observer } from 'mobx-react'
import TeamPaymentsViewModel from './TeamPaymentsViewModel'
import { Id } from 'types/definitions'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import PaymentMethodsEmptyState from 'billing/components/PaymentMethodsEmptyState'
import Icon from 'components/Icon'

interface TeamPaymentsProps {
  vm: TeamPaymentsViewModel
}

export default observer(function TeamPayments({ vm }: TeamPaymentsProps) {
  return (
    <ContentLayout accountForLabel accountForTabBar>
      {vm.activate.match({
        pending: () => <ContentLoading />,
        rejected: (err: unknown) => <ContentError error={err} />,
        resolved: () => (
          <>
            {vm.paymentMethods.length === 0 ? (
              <ContentEmpty>
                <PaymentMethodsEmptyState
                  canAddPaymentMethod={vm.canManageTeamBilling}
                  onNewPaymentMethod={vm.addPaymentMethod}
                />
              </ContentEmpty>
            ) : (
              <>
                <ContentMain>
                  <ListHeader count={vm.paymentMethods.length}>
                    Payment Methods
                  </ListHeader>
                  <BillingMethodList
                    onAddDisabled={vm.disabled}
                    onAddClick={
                      vm.canManageTeamBilling ? vm.addPaymentMethod : undefined
                    }
                  >
                    {vm.paymentMethods.map((method) => (
                      <BillingMethodListItem
                        key={Id.assert(method.id)}
                        billingMethod={method}
                        disabled={vm.disabled}
                        busy={
                          vm.setDefaultPaymentMethod.pending &&
                          vm.setDefaultPaymentMethod.args[0] === method
                        }
                        selected={method.default}
                        dropdownActions={
                          <>
                            {vm.canManageTeamBilling && (
                              <DropdownItem
                                onClick={() =>
                                  vm.setDefaultPaymentMethod(method)
                                }
                                disabled={vm.disabled || method.default}
                                icon={<Icon name="default-payment" />}
                              >
                                <DropdownItemTitle>
                                  Set as Current
                                </DropdownItemTitle>
                                <DropdownItemDescription>
                                  {method.default ? (
                                    <>
                                      This payment method is already the
                                      default.
                                    </>
                                  ) : (
                                    <>
                                      Future payments charged to this team will
                                      use this payment method.
                                    </>
                                  )}
                                </DropdownItemDescription>
                              </DropdownItem>
                            )}
                          </>
                        }
                      />
                    ))}
                  </BillingMethodList>
                </ContentMain>

                <ContentTip
                  illustrationUrl={require('assets/images/img_workspace-billing.svg')}
                  title="Transactions don't have to be complicated"
                >
                  Are you looking to get more out of your Workspace? Our
                  in-depth Help Center can help!
                </ContentTip>
              </>
            )}
          </>
        ),
      })}
    </ContentLayout>
  )
})
